import React, { useState } from 'react';
import axios from 'axios';
import API_URL from '../config';
import './LinehaulLandingPage.css';

const QUESTIONS = [
  { key: 'cdlClassA', label: 'Do you have a Class A CDL?' },
  { key: 'age23Plus', label: 'Are you 23 years of age or older?' },
  { key: 'experience2yr', label: 'Do you have 2+ years of verifiable commercial truck driving experience?' },
  { key: 'cleanRecord3yr', label: 'Do you have a clean driving record for the past 3 years?' },
  { key: 'passDrugBg', label: 'Are you willing and able to pass a drug screen and background check?' },
];

const isValidEmail = e => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e || '').trim());
const isValidPhone = p => String(p || '').replace(/\D/g, '').length >= 10;

function YesNoRow({ label, value, onChange }) {
  return (
    <div className="ll-q-row">
      <span className="ll-q-label">{label}</span>
      <div className="ll-q-toggle">
        <button type="button" className={`ll-q-btn${value === true ? ' active-yes' : ''}`} onClick={() => onChange(true)}>Yes</button>
        <button type="button" className={`ll-q-btn${value === false ? ' active-no' : ''}`} onClick={() => onChange(false)}>No</button>
      </div>
    </div>
  );
}

export default function LinehaulLandingPage() {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', company_website: '' });
  const [touched, setTouched] = useState({});
  const [answers, setAnswers] = useState({});

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const setField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const setTouchedField = (field) => setTouched(prev => ({ ...prev, [field]: true }));

  const emailValid = isValidEmail(formData.email);
  const phoneValid = isValidPhone(formData.phone);
  const allQuestionsAnswered = QUESTIONS.every(q => answers[q.key] === true || answers[q.key] === false);
  const canSubmit = formData.name.trim() && emailValid && phoneValid && allQuestionsAnswered && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitError('');
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/public/linehaul-leads/submit`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company_website: formData.company_website,
        answers,
        source: 'facebook_ad',
      });
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="ll-page">
        <div className="ll-hero">
          <div className="ll-hero-inner">
            <div className="ll-logo">⛰ Mountain Recruiting</div>
          </div>
        </div>
        <div className="ll-form-wrap">
          <div className="ll-success-card">
            <div className="ll-success-icon">✓</div>
            <h2>You're all set, {formData.name.split(' ')[0]}.</h2>
            <p>Thanks for applying for a FedEx Ground Linehaul driving position. A member of our recruiting team will reach out to you shortly.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ll-page">
      <div className="ll-hero">
        <div className="ll-hero-inner">
          <div className="ll-logo">⛰ Mountain Recruiting</div>
          <div className="ll-eyebrow">Now Hiring</div>
          <h1>FedEx Ground Linehaul Drivers</h1>
          <p className="ll-hero-sub">Apply in under 2 minutes. No resume required — just fill out the form below.</p>
        </div>
      </div>

      <div className="ll-form-wrap">
        <form className="ll-card" onSubmit={handleSubmit}>
          <input
            type="text"
            name="company_website"
            value={formData.company_website}
            onChange={e => setField('company_website', e.target.value)}
            autoComplete="off"
            tabIndex={-1}
            aria-hidden="true"
            className="ll-honeypot"
          />

          <h2 className="ll-section-title">Your Information</h2>

          <div className="ll-field">
            <label>Full Name</label>
            <input type="text" value={formData.name} onChange={e => setField('name', e.target.value)} required placeholder="John Smith" />
          </div>

          <div className="ll-field">
            <label>Email Address</label>
            <input
              type="email"
              value={formData.email}
              onChange={e => setField('email', e.target.value)}
              onBlur={() => setTouchedField('email')}
              required
              placeholder="you@example.com"
            />
            {touched.email && !emailValid && <div className="ll-error">Enter a valid email address.</div>}
          </div>

          <div className="ll-field">
            <label>Phone Number</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={e => setField('phone', e.target.value)}
              onBlur={() => setTouchedField('phone')}
              required
              placeholder="(555) 123-4567"
            />
            {touched.phone && !phoneValid && <div className="ll-error">Enter a valid 10-digit phone number.</div>}
          </div>

          <h2 className="ll-section-title">Minimum Requirements</h2>
          <div className="ll-questions">
            {QUESTIONS.map(q => (
              <YesNoRow
                key={q.key}
                label={q.label}
                value={answers[q.key]}
                onChange={v => setAnswers(prev => ({ ...prev, [q.key]: v }))}
              />
            ))}
          </div>

          {submitError && <div className="ll-error ll-submit-error">{submitError}</div>}

          <button type="submit" className="ll-submit-btn" disabled={!canSubmit}>
            {submitting ? 'Submitting…' : 'Submit Application'}
          </button>
        </form>
      </div>
    </div>
  );
}
